import { useState, forwardRef } from 'react';

import styled from '@emotion/styled';

import SwitchCase from '~components/SwitchCase';
import { DEPARTURE_TIME_OPTION_CONTAINER_PADDING } from '~constants/common';
import {
  DEFAULT_DEPARTURE_TIME,
  JOURNEY_TYPE,
  ROUND_TRIP_JOURNEY_TYPE,
} from '~constants/pointToPointTicket';

import type { RoundTripJourneyType } from '~models/Transportation/PointToPointTicket';

import MultipleTimeSelector from './MultipleTimeSelector';
import SingleTimeSelector from './SingleTimeSelector';

interface Props {
  isSingleTrip: boolean;
  outboundStartTime?: string | null;
  inboundStartTime?: string | null;
  onChangeTime: (type: RoundTripJourneyType, time: string) => void;
}

const DepartureTimeOption = forwardRef<HTMLUListElement, Props>(
  (
    {
      isSingleTrip,
      outboundStartTime = DEFAULT_DEPARTURE_TIME,
      inboundStartTime = DEFAULT_DEPARTURE_TIME,
      onChangeTime,
    }: Props,
    timeOptionRef,
  ) => {
    const [isOutboundSelectorOpen, setIsOutboundSelectorOpen] = useState(false);
    const [isInboundSelectOpen, setIsInboundSelectOpen] = useState(false);

    const handleChangeOutboundTime = (time: string) => () => {
      onChangeTime(ROUND_TRIP_JOURNEY_TYPE.OUTBOUND, time);
      setIsOutboundSelectorOpen(false);
    };

    const handleChangeInboundTime = (time: string) => () => {
      onChangeTime(ROUND_TRIP_JOURNEY_TYPE.INBOUND, time);
      setIsInboundSelectOpen(false);
    };

    const handleClickOutboundTime = () => {
      setIsOutboundSelectorOpen((prev) => !prev);
      setIsInboundSelectOpen(false);
    };

    const handleClickInboundTime = () => {
      setIsInboundSelectOpen((prev) => !prev);
      setIsOutboundSelectorOpen(false);
    };

    return (
      <Container>
        <SwitchCase
          value={isSingleTrip ? JOURNEY_TYPE.ONE_WAY : JOURNEY_TYPE.ROUND_TRIP}
          caseBy={{
            [JOURNEY_TYPE.ONE_WAY]: (
              <SingleTimeSelector
                ref={timeOptionRef}
                isOutboundSelectorOpen={isOutboundSelectorOpen}
                outboundTime={outboundStartTime}
                onClickOutboundTime={handleClickOutboundTime}
                onSelectOutboundTime={handleChangeOutboundTime}
              />
            ),
            [JOURNEY_TYPE.ROUND_TRIP]: (
              <MultipleTimeSelector
                ref={timeOptionRef}
                isOutboundSelectorOpen={isOutboundSelectorOpen}
                isInboundSelectOpen={isInboundSelectOpen}
                outboundTime={outboundStartTime}
                inboundTime={inboundStartTime}
                onClickOutboundTime={handleClickOutboundTime}
                onClickInboundTime={handleClickInboundTime}
                onSelectOutboundTime={handleChangeOutboundTime}
                onSelectInboundTime={handleChangeInboundTime}
              />
            ),
          }}
        />
      </Container>
    );
  },
);

DepartureTimeOption.displayName = 'DepartureTimeOption';

export default DepartureTimeOption;

const Container = styled.div`
  width: 100%;
  padding: ${DEPARTURE_TIME_OPTION_CONTAINER_PADDING}px 20px;
`;
